import { CandidateRejectionDialog } from "../../../../../common/components/functional/candidateRejection/CandidateRejectionDialog";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../../../app/hooks";
import BasicDialog from "../../../../../common/components/functional/dialogs/BasicDialog";
import { Nullable } from "../../../../../common/types/nullable";
import AskForInterestDialogContent from "../../../dialogs/AskForInterestDialogContent";
import SearchForOtherTalentsContent from "../../../dialogs/SearchForOtherTalentsContent";
import {
  LongOrderManualTalentSearchResult,
  PickedCandidate,
} from "../../../TalentsStatusAPI";
import {
  askWorkInterest,
  introduceToCustomer,
  chooseForInterview,
  pickCandidates,
  rejectCandidates,
  approveIntroducedTalents,
  withdrawTalent,
} from "../../../talentsStatusSlice";
import { CandidateGroupItem } from "../../../types/editOrder";
import { TalentStatusDialogContent } from "../../constants";
import ChooseForIntroductionDialogContent from "../../../dialogs/ChooseForIntroductionDialogContent";
import { isFulfilled } from "@reduxjs/toolkit";
import { hasPoolPro } from "../../../../talents/helpers";
import ChooseForInterviewDialogContent from "../../../dialogs/ChooseForInterviewDialogContent";
import WithdrawCandidateDialogContent from "../../../dialogs/WithdrawCandidateDialogContent";
import NotSuitableCandidateDialogContent from "../../../dialogs/NotSuitableCandidateDialogContent";

interface Props {
  open: boolean;
  content: Nullable<TalentStatusDialogContent>;
  onDialogClose: () => void;
  selectedTalents: CandidateGroupItem[];
  updateSelectedItems: (selectedItems: []) => void;
  bundleId: string;
  pickedCandidates: PickedCandidate[];
}

const TalentStatusDialogs = ({
  open,
  content,
  onDialogClose,
  selectedTalents,
  updateSelectedItems,
  bundleId,
  pickedCandidates,
}: Props) => {
  const [newSelectedTalents, setNewSelectedTalents] = useState<
    (CandidateGroupItem | LongOrderManualTalentSearchResult)[]
  >([]);

  const { t } = useTranslation("order");
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (content === TalentStatusDialogContent.ASK_FOR_INTEREST) {
      setNewSelectedTalents(selectedTalents.filter((t) => !hasPoolPro(t)));
    } else {
      // reset new selectedTalents if content changes
      setNewSelectedTalents([]);
    }
  }, [content, selectedTalents]);

  if (!content) return null;

  const onChange = (
    talent: CandidateGroupItem | LongOrderManualTalentSearchResult,
    checked: boolean,
  ) => {
    if ("id" in talent) {
      if (checked) {
        setNewSelectedTalents((prev) => [...prev, talent]);
      } else {
        setNewSelectedTalents((prev) =>
          prev.filter((t) => "id" in t && t.id !== talent.id),
        );
      }
      // LongOrderManualTalentSearchResult
    } else if ("talentId" in talent) {
      if (checked) {
        setNewSelectedTalents((prev) => [...prev, talent]);
      } else {
        setNewSelectedTalents((prev) =>
          prev.filter((t) => "talentId" in t && t.talentId !== talent.talentId),
        );
      }
    }
  };

  const contentComponents = {
    [TalentStatusDialogContent.ASK_FOR_INTEREST]: (
      <AskForInterestDialogContent
        newSelectedTalents={newSelectedTalents as CandidateGroupItem[]}
        selectedTalents={selectedTalents}
        onChange={onChange}
      />
    ),
    [TalentStatusDialogContent.SEARCH_OTHER_CANDIDATES]: (
      <SearchForOtherTalentsContent
        onChange={onChange}
        newSelectedTalents={
          newSelectedTalents as LongOrderManualTalentSearchResult[]
        }
        bundleId={bundleId}
      />
    ),
    [TalentStatusDialogContent.CHOOSE_FOR_INTRODUCTION]: (
      <ChooseForIntroductionDialogContent selectedTalents={selectedTalents} />
    ),
    [TalentStatusDialogContent.CHOOSE_FOR_INTERVIEW]: (
      <ChooseForInterviewDialogContent selectedTalents={selectedTalents} />
    ),
    [TalentStatusDialogContent.WITHDREW]: (
      <WithdrawCandidateDialogContent selectedTalents={selectedTalents} />
    ),
    [TalentStatusDialogContent.NOT_SUITABLE]: (
      <NotSuitableCandidateDialogContent selectedTalents={selectedTalents} />
    ),
    [TalentStatusDialogContent.CUSTOMER_REJECTED]: <></>,
  };

  const getTexts = (content: Nullable<TalentStatusDialogContent>) => {
    switch (content) {
      case TalentStatusDialogContent.ASK_FOR_INTEREST:
        return {
          title: t("askTalentsForInterest"),
          confirmLabel: t("ask"),
          contentText: t("selectedTalents"),
        };
      case TalentStatusDialogContent.SEARCH_OTHER_CANDIDATES:
        return { title: t("searchOtherTalents"), confirmLabel: t("pick") };
      case TalentStatusDialogContent.CHOOSE_FOR_INTRODUCTION:
        return {
          title: t("common:confirmSelection"),
          confirmLabel: t("common:confirm"),
        };
      case TalentStatusDialogContent.CHOOSE_FOR_INTERVIEW:
        return {
          title: t("common:confirmSelection"),
          confirmLabel: t("common:confirm"),
        };
      case TalentStatusDialogContent.WITHDREW:
        return {
          title: t("common:confirmSelection"),
          confirmLabel: t("common:confirm"),
        };
      case TalentStatusDialogContent.NOT_SUITABLE:
        return {
          title: t("common:confirmSelection"),
          confirmLabel: t("common:confirm"),
        };
      default:
        return { title: "", confirmLabel: "" };
    }
  };

  const handleConfirmAskForInterest = async () => {
    const talentIds = newSelectedTalents.map(
      (x) => (x as CandidateGroupItem).id,
    );

    await dispatch(
      askWorkInterest({
        bundleId,
        talentIds,
      }),
    ).then((action) => {
      if (isFulfilled(action)) {
        handleClose();
      }
    });
  };

  const handleClose = () => {
    onDialogClose();
    updateSelectedItems([]);
    setNewSelectedTalents([]);
  };

  const handleConfirmPickOtherCandidates = () => {
    const otherTalentSearchResults =
      newSelectedTalents as LongOrderManualTalentSearchResult[];

    const payload = {
      bundleId,
      talents: otherTalentSearchResults,
    };
    dispatch(pickCandidates(payload));
    onDialogClose();
    updateSelectedItems([]);
    setNewSelectedTalents([]);
  };

  const handleChooseCandidatesForIntroduction = () => {
    const payload = {
      bundleId,
      talentIds: selectedTalents.filter((t) => !hasPoolPro(t)).map((t) => t.id),
    };
    dispatch(introduceToCustomer(payload)).then((action) => {
      if (isFulfilled(action)) {
        handleClose();
      }
    });
  };

  const handleChooseCandidatesForInterview = () => {
    const payload = {
      bundleId,
      talentIds: selectedTalents.map((t) => t.id),
    };
    dispatch(chooseForInterview(payload)).then((action) => {
      if (isFulfilled(action)) {
        handleClose();
      }
    });
  };

  const handleWithdrawCandidate = () => {
    const payload = {
      bundleId,
      talentIds: selectedTalents.map((t) => t.id),
    };
    dispatch(withdrawTalent(payload)).then((action) => {
      if (isFulfilled(action)) {
        handleClose();
      }
    });
  };

  const handleMarkNotSuitable = () => {
    const payload = {
      bundleId,
      talentIds: selectedTalents.map((t) => t.id),
    };
    dispatch(rejectCandidates(payload)).then((action) => {
      if (isFulfilled(action)) {
        handleClose();
      }
    });
  };

  const getConfirmHandler = (content: Nullable<TalentStatusDialogContent>) => {
    switch (content) {
      case TalentStatusDialogContent.ASK_FOR_INTEREST:
        return handleConfirmAskForInterest;
      case TalentStatusDialogContent.SEARCH_OTHER_CANDIDATES:
        return handleConfirmPickOtherCandidates;
      case TalentStatusDialogContent.CHOOSE_FOR_INTRODUCTION:
        return handleChooseCandidatesForIntroduction;
      case TalentStatusDialogContent.CHOOSE_FOR_INTERVIEW:
        return handleChooseCandidatesForInterview;
      case TalentStatusDialogContent.WITHDREW:
        return handleWithdrawCandidate;
      case TalentStatusDialogContent.NOT_SUITABLE:
        return handleMarkNotSuitable;
      default:
        return () => {};
    }
  };

  const disableConfirm = (
    content: Nullable<TalentStatusDialogContent>,
  ): boolean => {
    switch (content) {
      case TalentStatusDialogContent.CHOOSE_FOR_INTRODUCTION:
        return selectedTalents.every((t) => hasPoolPro(t));
      case TalentStatusDialogContent.CHOOSE_FOR_INTERVIEW:
        return !selectedTalents.length;
      case TalentStatusDialogContent.WITHDREW:
        return !selectedTalents.length;
      case TalentStatusDialogContent.NOT_SUITABLE:
        return !selectedTalents.length;
      default:
        return !newSelectedTalents.length;
    }
  };

  if (
    (content === TalentStatusDialogContent.NOT_SUITABLE ||
      content === TalentStatusDialogContent.CUSTOMER_REJECTED) &&
    selectedTalents.length > 0
  ) {
    return (
      <CandidateRejectionDialog
        key={selectedTalents.map((talent) => talent.id).join(",")}
        open={open}
        talentName={selectedTalents.map((talent) => talent.fullName).join("\n")}
        candidateCount={selectedTalents.length}
        title={content === TalentStatusDialogContent.CUSTOMER_REJECTED ? t("customerRejected") : undefined}
        onClose={handleClose}
        onReject={async (details) => {
          const payload = {
            bundleId,
            talentIds: selectedTalents.map((talent) => talent.id),
            ...details,
          };
          const action = content === TalentStatusDialogContent.CUSTOMER_REJECTED
            ? await dispatch(approveIntroducedTalents({ ...payload, approved: false }))
            : await dispatch(rejectCandidates(payload));
          return isFulfilled(action);
        }}
      />
    );
  }

  return (
    <>
      <BasicDialog
        open={open}
        onCancel={onDialogClose}
        onConfirm={getConfirmHandler(content)}
        title={getTexts(content).title}
        confirmLabel={getTexts(content).confirmLabel}
        confirmDisabled={disableConfirm(content)}
        dialogContentText={getTexts(content).contentText}
        PaperProps={{ sx: { minWidth: "640px" } }}
        noBorder
        content={contentComponents[content]}
      />
    </>
  );
};

export default TalentStatusDialogs;
